import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Play, Filter } from 'lucide-react';
import axios from 'axios';
import FavoriteButton from '../components/FavoriteButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BrowsePage = () => {
  const [tutorials, setTutorials] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  const categories = [
    { id: 'all', name: 'Tout' },
    { id: 'makeup', name: 'Maquillage' },
    { id: 'hair', name: 'Coiffure' },
  ];

  useEffect(() => {
    fetchTutorials();
  }, [selectedCategory]);

  const fetchTutorials = async () => {
    setLoading(true);
    try {
      const url = selectedCategory === 'all' 
        ? `${API}/tutorials` 
        : `${API}/tutorials?category=${selectedCategory}`;
      const response = await axios.get(url);
      setTutorials(response.data);
    } catch (error) {
      console.error('Failed to fetch tutorials', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen pb-24 md:pb-12"
    >
      {/* Header */}
      <section className="py-12 md:py-20 border-b gold-border" data-testid="browse-header">
        <div className="container mx-auto px-6">
          <h1 className="font-serif text-5xl md:text-6xl text-charcoal tracking-tighter mb-4">
            Explorer les Tutoriels
          </h1>
          <p className="text-lg text-taupe max-w-2xl">
            Découvrez notre collection complète de tutoriels de maquillage et de coiffure
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-silk" data-testid="browse-filters">
        <div className="container mx-auto px-6">
          <div className="flex items-center gap-4 overflow-x-auto">
            <Filter className="w-5 h-5 text-primary flex-shrink-0" />
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                data-testid={`filter-${cat.id}`}
                className={`px-6 py-3 uppercase tracking-wider text-sm font-sans transition-all duration-300 whitespace-nowrap ${
                  selectedCategory === cat.id
                    ? 'bg-primary text-white'
                    : 'bg-transparent border gold-border text-charcoal hover:bg-primary hover:text-white'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Tutorials Grid */}
      <section className="py-12 md:py-20" data-testid="tutorials-grid">
        <div className="container mx-auto px-6">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-silk h-96 mb-4"></div>
                  <div className="bg-silk h-6 w-3/4 mb-2"></div>
                  <div className="bg-silk h-4 w-1/2"></div>
                </div>
              ))}
            </div>
          ) : tutorials.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-xl text-taupe">Aucun tutoriel disponible pour le moment.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {tutorials.map((tutorial, index) => (
                <motion.div
                  key={tutorial.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="group cursor-pointer relative"
                >
                  <Link to={`/tutorial/${tutorial.id}`} data-testid={`browse-tutorial-${tutorial.id}`}>
                    <div className="overflow-hidden border gold-border mb-4">
                      <div className="relative">
                        <img
                          src={tutorial.thumbnail}
                          alt={tutorial.title}
                          className="w-full h-80 object-cover image-zoom"
                        />
                        <div className="absolute inset-0 cinema-fade opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center">
                          <div className="w-16 h-16 bg-primary/90 flex items-center justify-center">
                            <Play className="w-8 h-8 text-white" fill="white" />
                          </div>
                        </div>
                        {tutorial.is_premium && (
                          <div className="absolute top-4 right-4 bg-primary px-3 py-1">
                            <span className="font-accent text-xs text-white uppercase tracking-wider">Premium</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <p className="font-accent text-xs uppercase tracking-wider text-primary mb-2">
                      {tutorial.category}
                    </p>
                    <h3 className="font-serif text-2xl text-charcoal tracking-tighter group-hover:text-primary transition-colors mb-2">
                      {tutorial.title}
                    </h3>
                    <p className="text-sm text-taupe line-clamp-2">{tutorial.description}</p>
                  </Link>
                  
                  <div className="absolute top-4 left-4 z-10">
                    <FavoriteButton tutorialId={tutorial.id} />
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </motion.div>
  );
};

export default BrowsePage;